import type { AcademyLink } from '#academy-virtual/types'
import type { DesignSectionSlug } from '@forgd/contract/types'

/**
 * Every page of the tool, in sidebar order.
 *
 * This replaces the ~230 lines of page copy embedded in v1's `useTD.ts` store, and is the single
 * source for the sidebar, the page header, Prev/Next and completion. Pages without a `path` are
 * not built yet: the sidebar shows them so the shape of the tool is visible, but they do not link
 * anywhere.
 */
export interface DesignPage {
  slug: DesignSectionSlug
  label: string
  /** Sub-path under the tool root; absent while the page is not built. */
  path?: string
  title: string
  description: string
  /**
   * Academy article behind "Readings & Resources". Typed against the generated union, so a link
   * that no longer exists fails the build rather than 404ing for a reader.
   */
  resource?: { path: AcademyLink, title: string }
}

export interface DesignSectionGroup {
  label: string
  pages: DesignPage[]
}

export const DESIGN_SECTIONS: DesignSectionGroup[] = [
  {
    label: 'Token Profile',
    pages: [
      {
        slug: 'basic-information',
        label: 'Basic Information',
        path: '/token-profile/basic-information',
        title: 'Basic Information',
        description:
          'Validate, and share details about your project. These details will be used on your token one-pager when published. Save & Commit changes to update the one-pager.',
        resource: {
          path: '/academy/how-to-guides/build-tokenomics-and-protocol-value-flows/tokenomics-101',
          title: 'Tokenomics 101',
        },
      },
      {
        slug: 'problem-and-solution',
        label: 'Problem & Solution',
        path: '/token-profile/problem-and-solution',
        title: 'Problem & Solution',
        description:
          'Describe the problem your project solves and the solution it offers. This frames everything else in your design.',
        resource: {
          path: '/academy/how-to-guides/build-tokenomics-and-protocol-value-flows/designing-the-core-business',
          title: 'Designing the Core Business',
        },
      },
      {
        slug: 'mission-vision',
        label: 'Mission & Vision',
        path: '/token-profile/mission-vision',
        title: 'Mission & Vision',
        description:
          'Your mission and vision extrapolate beyond a single product to the overarching business model of your project and its aspirational future state.',
        resource: {
          path: '/academy/how-to-guides/build-tokenomics-and-protocol-value-flows/memorializing-the-vision',
          title: 'Memorializing the Vision',
        },
      },
      {
        slug: 'value-creation',
        label: 'Value Creation',
        path: '/token-profile/value-creation',
        title: 'Value Creation',
        description: 'Define what value means for your project, and for whom it is created.',
        resource: {
          path: '/academy/how-to-guides/build-tokenomics-and-protocol-value-flows/value-flows-and-feedback-loops',
          title: 'Value Flows & Feedback Loops',
        },
      },
      {
        slug: 'business-model',
        label: 'Business Model',
        path: '/token-profile/business-model',
        title: 'Business Model',
        description:
          'The business model is intrinsically linked to your growth. Here you describe how you capture some of the value you create, and to whom it gets distributed.',
        resource: {
          path: '/academy/how-to-guides/build-tokenomics-and-protocol-value-flows/designing-the-core-business',
          title: 'Business Model',
        },
      },
      {
        slug: 'product-market-fit',
        label: 'Finding Product Market Fit',
        path: '/token-profile/product-market-fit',
        title: 'Finding Product Market Fit',
        description:
          'Complete an ecosystem assessment: who your users are, the tasks they perform, their motivations, the value they create, and the incentives you can offer to catalyze more of that behavior.',
        resource: {
          path: '/academy/how-to-guides/build-tokenomics-and-protocol-value-flows/finding-product-market-fit',
          title: 'Finding Product Market Fit',
        },
      },
      {
        slug: 'value-capture',
        label: 'Value Capture',
        path: '/token-profile/value-capture',
        title: 'Value Capture',
        description:
          'Describe how the protocol and the token each capture a share of the value your project creates.',
      },
    ],
  },
  {
    label: 'Modeling',
    pages: [
      {
        slug: 'token-distribution-schedule',
        label: 'Token Distribution Schedule',
        path: '/modeling/token-distribution-schedule',
        title: 'Token Distribution Schedule',
        description:
          'Allocate your supply across groups, and decide how each group unlocks. Every chart on the dashboard follows from this.',
      },
      {
        slug: 'token-emission-schedule',
        label: 'Token Emission Schedule',
        path: '/modeling/token-emission-schedule',
        title: 'Token Emission Schedule',
        description: 'Set how and when each group receives its tokens after TGE.',
      },
      {
        slug: 'demand-drivers',
        label: 'Demand Drivers',
        path: '/modeling/demand-drivers',
        title: 'Demand Drivers',
        description: 'Define the mechanisms and utilities that create demand for your token.',
      },
      {
        slug: 'estimating-demand',
        label: 'Estimating Demand',
        path: '/modeling/estimating-demand',
        title: 'Estimating Demand',
        description: 'Estimate the monthly dollar demand each driver generates.',
      },
    ],
  },
  {
    label: 'Valuation & Documenting',
    pages: [
      {
        slug: 'fdv-maximum-token-supply',
        label: 'FDV, Maximum Token Supply',
        path: '/valuation-documenting/fdv-maximum-token-supply',
        title: 'FDV & Maximum Token Supply',
        description:
          'Set your fully diluted valuation and maximum token supply, benchmarked against comparable projects.',
        resource: {
          path: '/academy/how-to-guides/build-tokenomics-and-protocol-value-flows/determining-your-valuation',
          title: 'Determining your Valuation',
        },
      },
    ],
  },
  {
    label: 'Adjusting',
    pages: [
      {
        slug: 'modeling-supply-and-demand',
        label: 'Modeling Supply & Demand',
        path: '/adjusting/modeling-supply-and-demand',
        title: 'Modeling Supply & Demand',
        description: 'Compare the supply entering the market against the demand absorbing it.',
      },
      {
        slug: 'optimizing-circulating-supply',
        label: 'Optimizing Circulating Supply',
        path: '/adjusting/optimizing-circulating-supply',
        title: 'Optimizing Circulating Supply',
        description: 'See how circulating supply grows and where it puts pressure on price.',
      },
      {
        slug: 'modeling-sell-pressure',
        label: 'Modeling Sell Pressure',
        path: '/adjusting/modeling-sell-pressure',
        title: 'Modeling Sell Pressure',
        description:
          'Set the cost basis and expected behaviour of each group to model where sell pressure comes from.',
      },
      {
        slug: 'simulating-price-discovery',
        label: 'Simulating Price Discovery',
        path: '/adjusting/simulating-price-discovery',
        title: 'Simulating Price Discovery',
        description: 'Simulate how price settles as supply and demand meet after launch.',
        resource: {
          path: '/academy/how-to-guides/plan-my-token-launch/price-discovery-in-the-secondary-market',
          title: 'Price Discovery in the Secondary Market',
        },
      },
      {
        slug: 'simulating-post-tge-pops',
        label: 'Simulating post-TGE Pops',
        path: '/adjusting/simulating-post-tge-pops',
        title: 'Simulating a Post-TGE "Pop"',
        description: 'Model how far above the listing price your token trades immediately after TGE.',
      },
    ],
  },
  {
    label: 'Publish',
    pages: [
      {
        slug: 'sanitizing-tokenomics',
        label: 'Manage One-Pager',
        path: '/publish/manage-one-pager',
        title: 'Manage One-Pager',
        description:
          'This page can be shared with your broader community. It contains all the details available in your tokenomics design. Publish your changes from this page when your data is ready, or when updates have been made.',
      },
    ],
  },
]

/** Built pages only, in order - what Prev/Next walks. */
export const DESIGN_PAGES: DesignPage[] = DESIGN_SECTIONS
  .flatMap(section => section.pages)
  .filter(page => Boolean(page.path))

export function findDesignPage(slug: DesignSectionSlug): DesignPage | undefined {
  return DESIGN_SECTIONS.flatMap(section => section.pages).find(page => page.slug === slug)
}

export function surroundingPages(slug: DesignSectionSlug): { prev?: DesignPage, next?: DesignPage } {
  const index = DESIGN_PAGES.findIndex(page => page.slug === slug)

  if (index === -1) {
    return {}
  }

  return { prev: DESIGN_PAGES[index - 1], next: DESIGN_PAGES[index + 1] }
}
